import classNames from "classnames";
import { useRouter } from "next/router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslations } from "use-intl";

import { useConfiguration } from "~/contexts/configuration";
import CrossCountry from "~/types/cross-country";
import CommonUtils from "~/utils/common-utils";
import powerReviewUtils, { PropsPowerReviewsCultureConfig } from "~/utils/power-reviews-utils";

import PowerReviewsUiScript from "../common/powerreviews-ui-script";
import ProductRatings, { ContextEnum } from "../products/product-ratings";
import styles from "./product-reviews-script.module.scss";

type Props = {
  selectedShade: CrossCountry.DetailedProduct | CrossCountry.ChildrenProduct;
  type: "mobile" | "desktop";
  parent_backend_id: string;
};

export default function ProductReviewsScript(props: Props) {
  const t = useTranslations();
  const router = useRouter();
  const { configuration } = useConfiguration();
  const [reviewsContainer, setReviewsContainer] = useState<HTMLElement | null>();

  const idCategorySnippet = props.type === "mobile" ? "pr-categorysnippet-mobile" : "pr-categorysnippet-desktop";

  const pwrConfig: PropsPowerReviewsCultureConfig = useMemo(
    () => powerReviewUtils.getPwrConfiguration(configuration, router),
    [configuration, router]
  );

  const isPowerReviewsFromEva = CommonUtils.parseBoolean(process.env.NEXT_PUBLIC_POWERREVIEWS_DISPLAY_DATA_FROM_EVA);
  const buttonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setReviewsContainer(document.getElementById("pr-reviewdisplay") || document.getElementById("product-reviews"));
  }, []);

  return (
    <>
      {/* PowerReviews UI (Reviews) Script */}
      <PowerReviewsUiScript parent_backend_id={props.parent_backend_id} idCategorySnippet={idCategorySnippet} />
      <div
        className={classNames(styles.reviews, props.type === "mobile" ? styles.mobileOnly : styles.desktopOnly)}
        key={props.type}
      >
        {isPowerReviewsFromEva && (
          <ProductRatings
            average={props.selectedShade.review_average_score || 0}
            context={ContextEnum.pdp}
            ref={buttonRef}
            idCategorySnippet={idCategorySnippet}
          />
        )}
        {pwrConfig && isPowerReviewsFromEva ? (
          <a
            href={`#${reviewsContainer?.id}`}
            onClick={(evt) => {
              evt.preventDefault();
              evt.stopPropagation();
              reviewsContainer?.scrollIntoView({ behavior: "smooth" });
            }}
            aria-label={t("generic.reviewsCount", { count: props.selectedShade?.review_total_number || 0 })}
          >
            <p className={styles.link}>
              {props.type === "desktop" ? (
                <>{t("generic.reviewsCount", { count: props.selectedShade?.review_total_number || 0 })}</>
              ) : (
                `(${props.selectedShade?.review_total_number || 0})`
              )}
            </p>
          </a>
        ) : null}
      </div>
    </>
  );
}
